import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Save, Shield, Loader2, Users } from "lucide-react";

interface AdminRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  createdAt: string;
}

interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  roleId: string | null;
  roleName: string | null;
  isSuperAdmin: boolean;
}

const PERMISSION_GROUPS = [
  {
    label: "Administration",
    items: [
      { key: "admin.overview", label: "Översikt" },
      { key: "admin.statistics", label: "Statistik" },
      { key: "admin.orders", label: "Ordrar" },
      { key: "admin.products", label: "Produkter" },
      { key: "admin.pricelists", label: "Prislistor" },
      { key: "admin.clients", label: "Kunder" },
      { key: "admin.returns", label: "Returer" },
      { key: "admin.invoicing", label: "Fakturering" },
    ],
  },
  {
    label: "Kommunikation",
    items: [
      { key: "comm.communication", label: "Kommunikation" },
      { key: "comm.support", label: "Support" },
      { key: "comm.notifications", label: "Notifikationer" },
    ],
  },
  {
    label: "Recept",
    items: [
      { key: "recipe.rawmaterials", label: "Råvarulista" },
      { key: "recipe.baserecipes", label: "Basrecept" },
      { key: "recipe.articlerecipes", label: "Artikelrecept" },
    ],
  },
  {
    label: "Produktion",
    items: [
      { key: "prod.tragare", label: "Trågare" },
      { key: "prod.bagare", label: "Bagare" },
      { key: "prod.pakare", label: "Pakare" },
    ],
  },
  {
    label: "Inställningar",
    items: [
      { key: "settings.settings", label: "Inställningar" },
      { key: "settings.users", label: "Användare" },
      { key: "settings.connections", label: "Kopplingar" },
      { key: "settings.api_docs", label: "API-docs" },
      { key: "settings.log", label: "Logg" },
      { key: "settings.roles", label: "Roller" },
    ],
  },
];

const ALL_KEYS = PERMISSION_GROUPS.flatMap(g => g.items.map(i => i.key));

export default function AdminRolesPage() {
  const { toast } = useToast();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPermissions, setEditPermissions] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { data: roles = [], isLoading: rolesLoading } = useQuery<AdminRole[]>({
    queryKey: ["/api/admin/roles"],
  });

  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users"],
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description: string; permissions: string[] }) =>
      apiRequest("POST", "/api/admin/roles", data).then(r => r.json() as Promise<AdminRole>),
    onSuccess: async (data: AdminRole) => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      setIsCreating(false);
      setSelectedRoleId(data.id);
      setIsDirty(false);
      toast({ title: "Roll skapad!" });
    },
    onError: (err: Error) => toast({ title: "Fel", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AdminRole> }) =>
      apiRequest("PATCH", `/api/admin/roles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      setIsDirty(false);
      toast({ title: "Roll sparad!" });
    },
    onError: (err: Error) => toast({ title: "Fel", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/roles/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setSelectedRoleId(null);
      setIsCreating(false);
      setDeleteDialogOpen(false);
      toast({ title: "Roll borttagen!" });
    },
    onError: (err: Error) => toast({ title: "Fel", description: err.message, variant: "destructive" }),
  });

  const assignMutation = useMutation({
    mutationFn: ({ adminId, roleId }: { adminId: string; roleId: string | null }) =>
      apiRequest("POST", "/api/admin/roles/assign", { adminId, roleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Roll tilldelad!" });
    },
    onError: (err: Error) => toast({ title: "Fel", description: err.message, variant: "destructive" }),
  });

  const openRole = (role: AdminRole) => {
    setSelectedRoleId(role.id);
    setEditName(role.name);
    setEditDescription(role.description);
    setEditPermissions([...role.permissions]);
    setIsCreating(false);
    setIsDirty(false);
  };

  const startCreate = () => {
    setSelectedRoleId(null);
    setEditName("");
    setEditDescription("");
    setEditPermissions([]);
    setIsCreating(true);
    setIsDirty(false);
  };

  const togglePermission = (key: string) => {
    setEditPermissions(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
    setIsDirty(true);
  };

  const toggleGroup = (keys: string[]) => {
    const allChecked = keys.every(k => editPermissions.includes(k));
    if (allChecked) {
      setEditPermissions(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setEditPermissions(prev => [...new Set([...prev, ...keys])]);
    }
    setIsDirty(true);
  };

  const toggleAll = () => {
    if (editPermissions.length === ALL_KEYS.length) {
      setEditPermissions([]);
    } else {
      setEditPermissions([...ALL_KEYS]);
    }
    setIsDirty(true);
  };

  const handleSave = () => {
    if (!editName.trim()) {
      toast({ title: "Rollnamn krävs", variant: "destructive" });
      return;
    }
    if (isCreating) {
      createMutation.mutate({ name: editName.trim(), description: editDescription.trim(), permissions: editPermissions });
    } else if (selectedRoleId) {
      updateMutation.mutate({ id: selectedRoleId, data: { name: editName.trim(), description: editDescription.trim(), permissions: editPermissions } });
    }
  };

  const usersForRole = selectedRoleId
    ? users.filter(u => u.roleId === selectedRoleId && !u.isSuperAdmin)
    : [];

  const nonSuperAdmins = users.filter(u => !u.isSuperAdmin);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Roller</h1>
        <p className="text-muted-foreground">Hantera roller och behörigheter för administratörer</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Role list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Roller</h2>
            <Button size="sm" onClick={startCreate} data-testid="button-create-role">
              <Plus className="h-4 w-4 mr-1" />
              Ny roll
            </Button>
          </div>

          {rolesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {roles.map(role => (
                <button
                  key={role.id}
                  onClick={() => openRole(role)}
                  data-testid={`button-role-${role.id}`}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedRoleId === role.id && !isCreating
                      ? "bg-primary/10 border-primary"
                      : "bg-card border-border hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{role.name}</p>
                      {role.description && (
                        <p className="text-xs text-muted-foreground truncate">{role.description}</p>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {role.permissions.length}
                    </Badge>
                  </div>
                </button>
              ))}
              {roles.length === 0 && !isCreating && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Inga roller skapade ännu
                </p>
              )}
              {isCreating && (
                <div className="p-3 rounded-lg border border-primary bg-primary/10">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Ny roll</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Edit panel */}
        <div className="lg:col-span-2 space-y-4">
          {(selectedRoleId || isCreating) ? (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {isCreating ? "Skapa ny roll" : "Redigera roll"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="role-name">Rollnamn</Label>
                    <Input
                      id="role-name"
                      value={editName}
                      onChange={e => { setEditName(e.target.value); setIsDirty(true); }}
                      placeholder="t.ex. Orderhanterare"
                      data-testid="input-role-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role-description">Beskrivning</Label>
                    <Textarea
                      id="role-description"
                      value={editDescription}
                      onChange={e => { setEditDescription(e.target.value); setIsDirty(true); }}
                      placeholder="Valfri beskrivning av rollens syfte"
                      rows={2}
                      data-testid="input-role-description"
                    />
                  </div>

                  {/* Permission checklist */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Behörigheter</Label>
                      <button
                        type="button"
                        onClick={toggleAll}
                        className="text-xs text-primary hover:underline"
                        data-testid="button-toggle-all-permissions"
                      >
                        {editPermissions.length === ALL_KEYS.length ? "Avmarkera alla" : "Markera alla"}
                      </button>
                    </div>
                    <div className="border rounded-lg divide-y">
                      {PERMISSION_GROUPS.map(group => {
                        const groupKeys = group.items.map(i => i.key);
                        const allChecked = groupKeys.every(k => editPermissions.includes(k));
                        const someChecked = groupKeys.some(k => editPermissions.includes(k));
                        return (
                          <div key={group.label} className="p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`group-${group.label}`}
                                checked={allChecked}
                                data-state={someChecked && !allChecked ? "indeterminate" : undefined}
                                onCheckedChange={() => toggleGroup(groupKeys)}
                                data-testid={`checkbox-group-${group.label}`}
                              />
                              <Label
                                htmlFor={`group-${group.label}`}
                                className="font-semibold text-sm cursor-pointer"
                              >
                                {group.label}
                              </Label>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 ml-6">
                              {group.items.map(item => (
                                <div key={item.key} className="flex items-center gap-2">
                                  <Checkbox
                                    id={item.key}
                                    checked={editPermissions.includes(item.key)}
                                    onCheckedChange={() => togglePermission(item.key)}
                                    data-testid={`checkbox-perm-${item.key}`}
                                  />
                                  <Label htmlFor={item.key} className="text-sm cursor-pointer font-normal">
                                    {item.label}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div>
                      {!isCreating && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeleteDialogOpen(true)}
                          data-testid="button-delete-role"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Ta bort roll
                        </Button>
                      )}
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={isSaving || !isDirty}
                      data-testid="button-save-role"
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {isCreating ? "Skapa roll" : "Spara ändringar"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Assigned users */}
              {!isCreating && selectedRoleId && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Tilldelade användare
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {usersForRole.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Inga användare har denna roll</p>
                    ) : (
                      <div className="space-y-2">
                        {usersForRole.map(user => (
                          <div key={user.id} className="flex items-center justify-between py-1" data-testid={`assigned-user-${user.id}`}>
                            <div>
                              <p className="text-sm font-medium">{user.displayName}</p>
                              <p className="text-xs text-muted-foreground">{user.username}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => assignMutation.mutate({ adminId: user.id, roleId: null })}
                              data-testid={`button-unassign-${user.id}`}
                            >
                              Ta bort
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Assign additional user */}
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Tilldela användare till denna roll:</p>
                      <Select
                        onValueChange={adminId => assignMutation.mutate({ adminId, roleId: selectedRoleId })}
                        data-testid="select-assign-user"
                      >
                        <SelectTrigger data-testid="trigger-assign-user">
                          <SelectValue placeholder="Välj användare..." />
                        </SelectTrigger>
                        <SelectContent>
                          {nonSuperAdmins
                            .filter(u => u.roleId !== selectedRoleId)
                            .map(user => (
                              <SelectItem key={user.id} value={user.id} data-testid={`option-user-${user.id}`}>
                                {user.displayName} ({user.username})
                                {user.roleName ? ` — ${user.roleName}` : ""}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Shield className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">Välj en roll att redigera eller skapa en ny</p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort roll?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort rollen "{editName}"? Alla användare som tilldelats rollen kommer att förlora den. Åtgärden kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedRoleId && deleteMutation.mutate(selectedRoleId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-role"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
